import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { fetchCarousel } from '@/services/api';
import { ChevronRight, Sparkles, MapPin, ArrowDown } from 'lucide-react';
import axios from 'axios';

interface CarouselItem {
  image: string;
  title: string;
  description: string;
}

const Hero = () => {
  const [carouselItems, setCarouselItems] = useState<CarouselItem[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [siteIcon, setSiteIcon] = useState<string | null>(null);

  useEffect(() => {
    const loadCarousel = async () => {
      try {
        const response = await fetchCarousel();
        const data = response.data.carouselItems;
        const validItems = data.filter((item: any) => item.image && item.title && item.description);
        setCarouselItems(validItems);
      } catch (error) {
        console.error('Failed to load carousel items:', error);
      }
    };
    loadCarousel();
  }, []);

  useEffect(() => {
    const fetchSiteIcon = async () => {
      try {
        const response = await axios.get('https://api.ibadansouthanglicandiocese.org.ng/api/v1/site_icon');
        setSiteIcon(response.data);
      } catch (error) {
        console.error('Failed to fetch site icon', error);
      }
    };
    fetchSiteIcon();
  }, []);

  useEffect(() => {
    if (carouselItems.length === 0) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [carouselItems]);

  const current = carouselItems[currentSlide];
  const imageUrl = current?.image.startsWith('http')
    ? current.image
    : `https://api.ibadansouthanglicandiocese.org.ng/img/${current?.image}`;

  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-slate-950">
      {/* Background Layer with Ken Burns Animation */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 0.5, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 2, ease: "easeOut" }}
          className="absolute inset-0 z-0"
        >
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url('${imageUrl}')` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-transparent to-slate-950" />
        </motion.div>
      </AnimatePresence>

      <div className="relative z-10 max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center w-full pt-20">

        {/* Left Content Area */}
        <div className="lg:col-span-7 space-y-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="inline-flex items-center gap-3 px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-full backdrop-blur-md"
          >
            <Sparkles className="w-4 h-4 text-amber-500" />
            <span className="text-amber-500 text-[10px] font-bold uppercase tracking-[0.3em]">Anglican Communion</span>
          </motion.div>

          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.8, ease: "circOut" }}
            >
              <h1 className="text-5xl lg:text-8xl font-serif font-bold text-white leading-[1.1] mb-6">
                {current?.title || "Welcome to Ibadan South"}
              </h1>
              <p className="text-lg lg:text-2xl text-slate-300 font-serif italic max-w-xl leading-relaxed">
                {current?.description || "Building God's kingdom through faith, fellowship, and service."}
              </p>
            </motion.div>
          </AnimatePresence>

          <div className="flex flex-wrap gap-5 pt-4">
            <Button asChild size="lg" className="h-14 px-8 bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold uppercase tracking-widest text-[11px] rounded-2xl transition-all hover:scale-105 active:scale-95">
              <Link to="/about">Our Journey <ChevronRight className="ml-2 w-4 h-4" /></Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="h-14 px-8 border-white/20 bg-white/5 backdrop-blur-md text-white hover:bg-white hover:text-slate-950 font-bold uppercase tracking-widest text-[11px] rounded-2xl transition-all">
              <Link to="/events">Join Us This Sunday</Link>
            </Button>
          </div>
        </div>

        {/* Right Decorative Area */}
        <div className="lg:col-span-5 hidden lg:flex justify-end relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.8, rotate: -5 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            className="relative"
          >
            {/* Main Shield/Motto Card */}
            <div className="w-80 h-96 bg-white/10 backdrop-blur-xl border border-white/20 rounded-[3rem] p-8 flex flex-col items-center justify-center text-center shadow-2xl relative z-10">
              <div className="w-20 h-20 bg-amber-500 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/20 mb-6 rotate-3">
                {siteIcon ? (
                  <img src={siteIcon} alt="Icon" className="w-12 h-12 object-contain" />
                ) : (
                  <span className="text-4xl text-slate-950 font-bold">✝</span>
                )}
              </div>
              <h3 className="text-white font-serif text-2xl font-bold mb-4">"The Joy of the Lord"</h3>
              <div className="h-px w-12 bg-amber-500 mb-4" />
              <p className="text-amber-500 font-bold uppercase tracking-widest text-[10px]">Our Strength</p>

              {/* Subtle Location Tag */}
              <div className="mt-12 flex items-center gap-2 text-slate-400 text-[10px] uppercase font-bold tracking-tighter">
                <MapPin size={12} className="text-amber-500" />
                <span>Ibadan, Nigeria</span>
              </div>
            </div>

            {/* Background Decorative Rings */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] border border-white/5 rounded-full z-0" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] border border-white/5 rounded-full z-0" />
          </motion.div>
        </div>
      </div>

      {/* Progress Indicators */}
      <div className="absolute bottom-12 left-6 flex gap-3 z-20">
        {carouselItems.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentSlide(idx)}
            className={`h-1 transition-all duration-500 rounded-full ${currentSlide === idx ? 'w-12 bg-amber-500' : 'w-4 bg-white/20'
              }`}
          />
        ))}
      </div>

      {/* Scroll Down Hint */}
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 text-white/30 hidden md:block"
      >
        <ArrowDown size={24} />
      </motion.div>
    </section>
  );
};

export default Hero;